import React, { useState, useEffect, useMemo } from "react";
import PhotoReleaseQuestion, { usePhotoReleaseQuestion } from "../waivers/PhotoReleaseQuestion";
import { computeAgeOnDate } from "../../utils/ageEligibility";
import { Input, Textarea } from "../ui";
import { formatPartialNational } from "../../utils/phoneFormat";
import PhoneInput from "../ui/PhoneInput";
import { useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import PublicPaymentStep from "../common/PublicPaymentStep";
import {
  usePublicMemberLookup,
  isAthleteUuid,
} from "../../hooks/usePublicMemberLookup";
import {
  Calendar,
  MapPin,
  Users,
  DollarSign,
  AlertCircle,
  CheckCircle2,
  ArrowLeft,
  Clock,
  Loader2,
  Plus,
  Trash2,
} from "lucide-react";
import Navbar from "../layout/Navbar";
import Footer from "../layout/Footer";
import { postEmbedSignal } from "../../utils/embedSignals";
import SEOHead from "../seo/SEOHead";
import { byType, typedEventNames, TypedEventType } from "../../utils/typedEvents";
import { publicTypedEventsApiFor } from "../../api/typedEventsApiFor";
import type { PublicTypedEvent, GuestRegistrationData, BatchGuestRegistrationData } from "../../api/publicTypedEventsAPI";
import { safeSessionGetItem } from "@mediatechgroup/app-version";
import { skillLevelLabel, typedEventTypeLabel } from "../../utils/typedEventLabels";

/**
 * Full, with a waitlist: the server puts a new sign-up on the waitlist and
 * takes no payment (services/eventRegistration: mode "none" while
 * waitlisted). The list said "Waitlist available" while this page offered
 * "Register & Pay $135.00" (2026-10-10). A max of 0 means uncapped.
 */
export function joinsWaitlist(ev: { max_registrations?: number | null; current_registrations?: number | null; waitlist_enabled?: boolean | null } | null | undefined): boolean {
  if (!ev || !ev.max_registrations || !ev.waitlist_enabled) return false;
  return (ev.current_registrations ?? 0) >= ev.max_registrations;
}

/**
 * PublicCampPage and PublicClinicPage, one implementation (Phase B4,
 * backend docs/EVENT_PRIMITIVE_CONSOLIDATION.md). Built per type; the camps
 * and clinics files are the entry points routes and tests import.
 */
export function createPublicTypedEventPage(type: TypedEventType) {
  const T = typedEventNames(type);
  const { getPublicTypedEventById, registerGuestForTypedEvent, batchRegisterGuestForTypedEvent, confirmTypedEventPayment } = publicTypedEventsApiFor(type);

  interface Participant {
    first_name: string;
    last_name: string;
    date_of_birth: string;
    gender: string;
    athlete_id?: string;
  }

  // Inner form component that uses Stripe hooks
  const RegistrationForm: React.FC<{
    typedEvent: PublicTypedEvent;
    clubid: string;
    embed?: boolean;
  }> = ({ typedEvent, clubid, embed }) => {
    // Stripe hooks are no longer needed at this level — payment lives in
    // <PublicPaymentStep>, which owns its own <Elements>.
    const waitlistOnly = joinsWaitlist(typedEvent);

    // Detect embed dark theme from URL params or sessionStorage
    const isEmbedDark = useMemo(() => {
      if (!embed) return false;
      const params = new URLSearchParams(window.location.search);
      const theme = params.get("theme");
      if (theme) return theme === "dark";
      try {
        const stored = safeSessionGetItem("embedStyleOverrides");
        if (stored) return JSON.parse(stored).theme === "dark";
      } catch { /* ignore */ }
      return false;
    }, [embed]);

    const [step, setStep] = useState<"form" | "payment" | "success">("form");
    const [submitting, setSubmitting] = useState(false);
    // The club's photo / media question (optional; never blocks registering).
    const photo = usePhotoReleaseQuestion("public");
    const [registrationResult, setRegistrationResult] = useState<any>(null);
    // True when we landed on the success step via a Stripe redirect return
    // (3DS / SCA) rather than the inline flow — the in-memory participant
    // state is gone, so the success screen shows a generic confirmation.
    const [completedViaRedirect, setCompletedViaRedirect] = useState(false);

    // Contact info (shared)
    const [contactInfo, setContactInfo] = useState({
      contact_email: "",
      contact_phone: "",
      contact_name: "",
    });

    // Multi-participant state
    const [participants, setParticipants] = useState<Participant[]>([
      { first_name: "", last_name: "", date_of_birth: "", gender: "" },
    ]);

    // Emergency & medical (shared)
    const [emergencyInfo, setEmergencyInfo] = useState({
      emergency_contact_name: "",
      emergency_contact_phone: "",
      emergency_contact_relationship: "",
      medical_info: "",
      allergies: "",
      medications: "",
      dietary_restrictions: "",
      notes: "",
    });

    const [selectedPricing, setSelectedPricing] = useState(
      typedEvent.pricing_options?.[0] || null
    );

    // Member/customer identity. The lookup is a two-step dance: /lookup-member
    // returns only requires_password=true, then the visitor enters their password
    // and /verify-member releases PII. That password gate is a SECURITY boundary,
    // and it lives in one shared hook rather than a copy per public page — see
    // hooks/usePublicMemberLookup.
    const {
      emailLookupLoading,
      memberFound,
      accountType,
      membershipId,
      memberToken,
      memberAthletes,
      selectedAthleteIds,
      passwordPromptOpen,
      verifyPassword,
      verifyError,
      verifyLoading,
      setVerifyPassword,
      setSelectedAthleteIds,
      resetMemberState,
      handleEmailBlur,
      handleVerifyPassword,
      handleSkipPasswordPrompt,
      toggleAthleteSelection,
    } = usePublicMemberLookup({
      clubid: clubid || "",
      resource: T.nouns,
      setContactInfo,
      setParticipants,
    });

    // Handle the return from a Stripe redirect (3DS / SCA). When
    // confirmPayment needs to authenticate off-page it navigates to
    // return_url (this page) and appends `payment_intent`,
    // `payment_intent_client_secret`, and `redirect_status`. Without
    // this handler the page just remounts on the empty form and the
    // customer never sees a confirmation even though the charge
    // succeeded. See PublicPaymentStep for the confirm call.
    useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      const paymentIntentId = params.get("payment_intent");
      const redirectStatus = params.get("redirect_status");
      if (!paymentIntentId || !redirectStatus) return;

      // Strip the Stripe params so a refresh doesn't re-trigger this.
      const cleanUrl = window.location.pathname +
        (window.location.hash || "");
      window.history.replaceState({}, "", cleanUrl);

      if (redirectStatus === "succeeded" || redirectStatus === "processing") {
        // Best-effort server fulfillment; the Stripe webhook reconciles
        // regardless, so a failure here must not block the success screen.
        confirmTypedEventPayment(clubid, typedEvent.id, paymentIntentId).catch(() => {
          /* webhook will reconcile */
        });
        setCompletedViaRedirect(true);
        setStep("success");
      } else {
        toast.error(
          "Payment was not completed. Please try registering again."
        );
      }
    }, [clubid, typedEvent.id]);

    // Calculate age as of the camp start date (not today)
    // (utils/ageEligibility — both dates are calendar days, never UTC instants).
    const calculateAge = (dob: string): number =>
      computeAgeOnDate(dob, typedEvent.dates?.[0]?.date ?? null) ?? NaN;

    const handleContactChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
      const { name, value } = e.target;
      setContactInfo((prev) => ({ ...prev, [name]: value }));
      if (name === "contact_email") {
        resetMemberState();
      }
    };

    const handleEmergencyChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
      const { name, value } = e.target;
      setEmergencyInfo((prev) => ({ ...prev, [name]: value }));
    };


    const addManualParticipant = () => {
      if (participants.length >= 10) {
        toast.error("Maximum 10 participants allowed");
        return;
      }
      setParticipants((prev) => [
        ...prev,
        { first_name: "", last_name: "", date_of_birth: "", gender: "" },
      ]);
    };

    const updateParticipant = (index: number, field: keyof Participant, value: string) => {
      setParticipants((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      });
    };

    const removeParticipant = (index: number) => {
      const p = participants[index];
      if (p.athlete_id) {
        setSelectedAthleteIds((prev) => {
          const next = new Set(prev);
          next.delete(p.athlete_id!);
          return next;
        });
      }
      setParticipants((prev) => prev.filter((_, i) => i !== index));
    };

    // Build effective participant list from athletes + manual entries
    const effectiveParticipants = participants;

    const getTotalAmount = (): number => {
      const unitPrice = selectedPricing?.amount || 0;
      return unitPrice * Math.max(effectiveParticipants.length, 1);
    };

    const validateForm = () => {
      if (!contactInfo.contact_email) {
        toast.error("Contact email is required");
        return false;
      }

      // Email matched an existing member/customer but the visitor
      // hasn't verified with their password yet. Block submission so
      // they can authenticate — without this they'd fall through to
      // the public guest-create path which collides on the unique
      // memberships.email index and silently fails the registration.
      // After verify, registration links to their member account and
      // their saved payment methods become available on the next step.
      if (passwordPromptOpen && !memberFound) {
        toast.error("This email already has an account. Enter your password above to continue, or use a different email.");
        return false;
      }

      if (effectiveParticipants.length === 0) {
        toast.error("At least one participant is required");
        return false;
      }

      for (let i = 0; i < effectiveParticipants.length; i++) {
        const p = effectiveParticipants[i];
        const label = p.first_name && p.last_name ? `${p.first_name} ${p.last_name}` : `Participant ${i + 1}`;
        if (!p.first_name || !p.last_name) {
          toast.error(`${label}: first and last name are required`);
          return false;
        }
        if (!p.date_of_birth) {
          toast.error(`${label}: date of birth is required`);
          return false;
        }
        if (!p.gender) {
          toast.error(`${label}: gender is required`);
          return false;
        }
        // Age validation
        if (typedEvent.age_min || typedEvent.age_max) {
          const age = calculateAge(p.date_of_birth);
          if (typedEvent.age_min && age < typedEvent.age_min) {
            toast.error(`${label} must be at least ${typedEvent.age_min} years old`);
            return false;
          }
          if (typedEvent.age_max && age > typedEvent.age_max) {
            toast.error(`${label} must be ${typedEvent.age_max} years old or under`);
            return false;
          }
        }
      }

      return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!validateForm()) return;

      setSubmitting(true);
      try {
        const N = effectiveParticipants.length;

        if (N === 1) {
          // Single participant → use existing endpoint
          const p = effectiveParticipants[0];
          const registrationData: GuestRegistrationData = {
            contact_email: contactInfo.contact_email,
            contact_phone: contactInfo.contact_phone,
            contact_name: contactInfo.contact_name || `${p.first_name} ${p.last_name}`,
            first_name: p.first_name,
            last_name: p.last_name,
            date_of_birth: p.date_of_birth,
            gender: p.gender,
            pricing_option: selectedPricing || undefined,
            emergency_contact_name: emergencyInfo.emergency_contact_name || undefined,
            emergency_contact_phone: emergencyInfo.emergency_contact_phone || undefined,
            emergency_contact_relationship: emergencyInfo.emergency_contact_relationship || undefined,
            medical_info: emergencyInfo.medical_info || undefined,
            allergies: emergencyInfo.allergies || undefined,
            medications: emergencyInfo.medications || undefined,
            dietary_restrictions: emergencyInfo.dietary_restrictions || undefined,
            notes: emergencyInfo.notes || undefined,
            membership_id: membershipId || undefined,
            member_token: memberToken || undefined,
          };

          const response = await registerGuestForTypedEvent(clubid, typedEvent.id, { ...registrationData, ...photo.payload });

          if (response?.success) {
            setRegistrationResult(response);
            if (response.clientSecret) {
              setStep("payment");
            } else {
              setStep("success");
              toast.success(response.message || "Successfully registered!");
            }
          } else {
            toast.error(response?.message || "Registration failed");
          }
        } else {
          // Multi-participant → use batch endpoint
          const batchData: BatchGuestRegistrationData = {
            contact_email: contactInfo.contact_email,
            contact_phone: contactInfo.contact_phone,
            contact_name: contactInfo.contact_name || `${effectiveParticipants[0].first_name} ${effectiveParticipants[0].last_name}`,
            participants: effectiveParticipants.map((p) => ({
              first_name: p.first_name,
              last_name: p.last_name,
              date_of_birth: p.date_of_birth,
              gender: p.gender,
              athlete_id: p.athlete_id,
            })),
            pricing_option: selectedPricing || undefined,
            emergency_contact_name: emergencyInfo.emergency_contact_name || undefined,
            emergency_contact_phone: emergencyInfo.emergency_contact_phone || undefined,
            emergency_contact_relationship: emergencyInfo.emergency_contact_relationship || undefined,
            medical_info: emergencyInfo.medical_info || undefined,
            allergies: emergencyInfo.allergies || undefined,
            medications: emergencyInfo.medications || undefined,
            dietary_restrictions: emergencyInfo.dietary_restrictions || undefined,
            notes: emergencyInfo.notes || undefined,
            membership_id: membershipId || undefined,
            member_token: memberToken || undefined,
          };

          const response = await batchRegisterGuestForTypedEvent(clubid, typedEvent.id, { ...batchData, ...photo.payload });

          if (response?.success) {
            setRegistrationResult(response);
            if (response.clientSecret) {
              setStep("payment");
            } else {
              setStep("success");
              toast.success(response.message || "Successfully registered!");
            }
          } else {
            toast.error(response?.message || "Registration failed");
          }
        }
      } catch (err: any) {
        const message = err.response?.data?.message || err.message || "Registration failed";
        toast.error(message);
      } finally {
        setSubmitting(false);
      }
    };

    // Payment is handled by <PublicPaymentStep>, which owns its own
    // <Elements> initialized with the PI's clientSecret so saved
    // payment methods (when the visitor authenticated against an
    // existing member) surface automatically.

    if (step === "success") {
      if (embed) postEmbedSignal("clubcentral:complete", { widget: "event" });
      const names = effectiveParticipants.map((p) => `${p.first_name} ${p.last_name}`).join(", ");
      return (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Registration Complete!</h3>
          {completedViaRedirect ? (
            <p className="text-gray-600 mb-2">
              Your payment was received and your registration for {typedEvent.name} is confirmed.
            </p>
          ) : (
            <>
              <p className="text-gray-600 mb-2">
                {effectiveParticipants.length === 1
                  ? `${names} has been registered for ${typedEvent.name}.`
                  : `${effectiveParticipants.length} participants have been registered for ${typedEvent.name}.`}
              </p>
              {effectiveParticipants.length > 1 && (
                <p className="text-gray-500 text-sm mb-2">{names}</p>
              )}
            </>
          )}
          <p className="text-gray-500 text-sm mb-6">
            {completedViaRedirect
              ? "A confirmation email is on its way."
              : `A confirmation email will be sent to ${contactInfo.contact_email}.`}
          </p>
          <Link
            to={embed ? `/${clubid}/embed/${T.nouns}` : `/${clubid}/${T.nouns}`}
            className="inline-flex items-center px-4 py-2 bg-[var(--cc-action)] text-[var(--cc-action-text)] hover:bg-[var(--cc-action-hover)] rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to {T.Nouns}
          </Link>
        </div>
      );
    }

    if (step === "payment" && registrationResult?.clientSecret) {
      const totalAmt = getTotalAmount();
      const p0 = effectiveParticipants[0];
      return (
        <PublicPaymentStep
          clientSecret={registrationResult.clientSecret}
          totalAmt={totalAmt}
          participantCount={effectiveParticipants.length}
          unitPrice={selectedPricing?.amount}
          billingName={
            contactInfo.contact_name || `${p0?.first_name || ""} ${p0?.last_name || ""}`.trim() || undefined
          }
          billingEmail={contactInfo.contact_email}
          isEmbedDark={isEmbedDark}
          onPaymentSucceeded={async (paymentIntentId) => {
            await confirmTypedEventPayment(clubid, typedEvent.id, paymentIntentId);
          }}
          onComplete={() => setStep("success")}
        />
      );
    }

    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Contact Information */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Contact Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label
                htmlFor={`${T.noun}-contact-email`}
                className={`block text-sm font-medium mb-1 ${isEmbedDark ? "text-gray-300" : "text-gray-700"}`}
              >
                Email *
              </label>
              <div className="relative">
                <input
                  id={`${T.noun}-contact-email`}
                  type="email"
                  name="contact_email"
                  value={contactInfo.contact_email}
                  onChange={handleContactChange}
                  onBlur={() => handleEmailBlur(contactInfo.contact_email)}
                  className={`w-full border rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 ${
                  isEmbedDark
                    ? "border-gray-600 bg-gray-900 text-gray-100"
                    : "border-gray-300"
                }`}
                  required
                  placeholder="Enter your email to check for existing membership"
                />
                {emailLookupLoading && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-blue-500" />
                )}
              </div>
              {passwordPromptOpen && !memberFound && (
                <div className={`mt-2 p-3 rounded-md border ${isEmbedDark ? "border-blue-700 bg-blue-950/20" : "border-blue-200 bg-blue-50"}`}>
                  <p className={`text-sm mb-2 ${isEmbedDark ? "text-gray-100" : "text-gray-800"}`}>
                    We found an existing account for this email. Enter its password to register with that account (use Forgot password on the sign-in page if you need a new one), or use a different email address to register as a new visitor.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="password"
                      aria-label="Password"
                      value={verifyPassword}
                      onChange={(e) => setVerifyPassword(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleVerifyPassword(contactInfo.contact_email);
                        }
                      }}
                      placeholder="Password"
                      autoComplete="current-password"
                      className={`flex-1 border rounded-md px-3 py-2 text-sm ${
                      isEmbedDark
                        ? "border-gray-600 bg-gray-900 text-gray-100"
                        : "border-gray-300"
                    }`}
                    />
                    <button
                      type="button"
                      onClick={() => handleVerifyPassword(contactInfo.contact_email)}
                      disabled={verifyLoading || !verifyPassword}
                      className="px-3 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                    >
                      {verifyLoading ? "Verifying…" : "Verify"}
                    </button>
                    <button
                      type="button"
                      onClick={handleSkipPasswordPrompt}
                      className={`px-3 py-2 rounded-md text-sm ${
                      isEmbedDark
                        ? "text-gray-300 hover:bg-gray-800"
                        : "text-gray-700 hover:bg-gray-200"
                    }`}
                    >
                      Skip
                    </button>
                  </div>
                  {verifyError && (
                    <p className="mt-2 text-xs text-red-600">{verifyError}</p>
                  )}
                </div>
              )}
            </div>
            <div>
              <Input
                label="Your Name"
                type="text"
                name="contact_name"
                value={contactInfo.contact_name}
                onChange={handleContactChange}
                className="border-gray-300"
                placeholder="If different from participant"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <PhoneInput
                name="contact_phone"
                value={contactInfo.contact_phone}
                onChange={(d) =>
                  handleContactChange({
                    target: { name: "contact_phone", value: formatPartialNational(d) },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Member/Customer Found Banner */}
        {memberFound && (
          <div className={`rounded-lg border p-4 flex items-start gap-3 ${
          isEmbedDark
            ? "bg-green-900/30 border-green-700"
            : "bg-green-50 border-green-200"
        }`}>
            <CheckCircle2 className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isEmbedDark ? "text-green-400" : "text-green-600"}`} />
            <div>
              <p className={`font-medium ${isEmbedDark ? "text-green-300" : "text-green-800"}`}>
                Welcome back{contactInfo.contact_name ? `, ${contactInfo.contact_name.split(" ")[0]}` : ""}!
              </p>
              <p className={`text-sm ${isEmbedDark ? "text-green-400" : "text-green-600"}`}>
                {accountType === "customer"
                  ? memberAthletes.length > 0
                    ? "We found your account. Select from your previous participants or register someone new."
                    : "We found your account."
                  : "We found your membership. Select participants below."}
              </p>
            </div>
          </div>
        )}

        {/* Athlete Selector - Checkboxes for multi-select */}
        {memberFound && memberAthletes.length > 0 && (
          <div className={`rounded-lg border p-6 ${
          isEmbedDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        }`}>
            <h3 className={`text-lg font-semibold mb-4 ${isEmbedDark ? "text-gray-100" : "text-gray-900"}`}>
              Select Participants
            </h3>
            <div className="space-y-2">
              {memberAthletes.map((athlete) => {
                const isSelected = selectedAthleteIds.has(athlete.id);
                // Check age eligibility
                let ageReason = "";
                if (athlete.date_of_birth && (typedEvent.age_min || typedEvent.age_max)) {
                  const age = calculateAge(athlete.date_of_birth);
                  if (typedEvent.age_min && age < typedEvent.age_min) {
                    ageReason = `Too young (age ${age})`;
                  } else if (typedEvent.age_max && age > typedEvent.age_max) {
                    ageReason = `Too old (age ${age})`;
                  }
                }

                return (
                  <label
                    key={athlete.id}
                    className={`flex items-center p-3 border rounded-lg transition-colors ${
                    ageReason
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  } ${
                    isSelected
                      ? isEmbedDark
                        ? "border-blue-500 bg-blue-900/30"
                        : "border-blue-500 bg-blue-50"
                      : isEmbedDark
                        ? "border-gray-600 hover:border-gray-500"
                        : "border-gray-200 hover:border-gray-300"
                  }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => !ageReason && toggleAthleteSelection(athlete)}
                      disabled={!!ageReason}
                      className="mr-3 w-4 h-4 rounded"
                    />
                    <span className={`font-medium flex-1 ${isEmbedDark ? "text-gray-100" : "text-gray-900"}`}>
                      {athlete.first_name} {athlete.last_name}
                      {athlete.date_of_birth && (
                        <span className={`text-sm font-normal ml-2 ${isEmbedDark ? "text-gray-400" : "text-gray-500"}`}>
                          (Age {calculateAge(athlete.date_of_birth)})
                        </span>
                      )}
                    </span>
                    {ageReason && (
                      <span className="text-xs text-red-500 ml-2">{ageReason}</span>
                    )}
                    {isSelected && !ageReason && (
                      <CheckCircle2 className="w-5 h-5 text-blue-500 ml-2 flex-shrink-0" />
                    )}
                  </label>
                );
              })}
            </div>
            <button
              type="button"
              onClick={addManualParticipant}
              className={`mt-3 w-full p-2.5 rounded-lg border-2 border-dashed flex items-center justify-center gap-2 text-sm font-medium transition-colors ${
              isEmbedDark
                ? "border-gray-600 text-gray-400 hover:border-gray-500"
                : "border-gray-300 text-gray-500 hover:border-gray-400"
            }`}
            >
              <Plus className="w-4 h-4" />
              Register someone new
            </button>
          </div>
        )}

        {/* Participant Information - Cards for each participant */}
        {(!memberFound || memberAthletes.length === 0) && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {participants.length > 1 ? "Participants" : "Participant Information"}
            </h3>

            <div className="space-y-4">
              {participants.map((p, index) => (
                <div key={index} className={`${participants.length > 1 ? "p-4 bg-gray-50 rounded-lg border border-gray-100" : ""}`}>
                  {participants.length > 1 && (
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm font-medium text-gray-700">
                        {p.first_name && p.last_name ? `${p.first_name} ${p.last_name}` : `Participant ${index + 1}`}
                      </span>
                      {index > 0 && (
                        <button aria-label={`Remove participant ${index + 1}`}
                          type="button"
                          onClick={() => removeParticipant(index)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Input
                        label="First Name *"
                        type="text"
                        value={p.first_name}
                        onChange={(e) => updateParticipant(index, "first_name", e.target.value)}
                        className="border-gray-300"
                        required={index === 0}
                      />
                    </div>
                    <div>
                      <Input
                        label="Last Name *"
                        type="text"
                        value={p.last_name}
                        onChange={(e) => updateParticipant(index, "last_name", e.target.value)}
                        className="border-gray-300"
                        required={index === 0}
                      />
                    </div>
                    <div>
                      <Input
                        label="Date of Birth *"
                        type="date" autoComplete="off"
                        value={p.date_of_birth}
                        onChange={(e) => updateParticipant(index, "date_of_birth", e.target.value)}
                        className="border-gray-300"
                        required={index === 0}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Gender *
                      </label>
                      <select
                        value={p.gender}
                        onChange={(e) => updateParticipant(index, "gender", e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        required={index === 0}
                      >
                        <option value="">Select...</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        {typedEvent.allow_other_gender && (
                          <>
                            <option value="other">Other</option>
                            <option value="prefer_not_to_say">Prefer not to say</option>
                          </>
                        )}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={addManualParticipant}
              className="mt-4 w-full p-2.5 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 flex items-center justify-center gap-2 text-sm font-medium hover:border-gray-400 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Another Participant
            </button>
          </div>
        )}

        {/* Manual participant cards when member found (for "Register someone new" entries).
            Exclude rows that correspond to a selected synthetic-id athlete (no athlete_id
            on the participant, but first/last/dob match a selectedAthleteIds entry from
            memberAthletes) so they don't double-render alongside the checkbox above. */}
        {(() => {
          const syntheticSelectedKeys = new Set(
            memberAthletes
              .filter((a) => selectedAthleteIds.has(a.id) && !isAthleteUuid(a.id))
              .map((a) => `${a.first_name}-${a.last_name}-${a.date_of_birth}`)
          );
          const renderable = participants.filter(
            (p) => !p.athlete_id && !syntheticSelectedKeys.has(`${p.first_name}-${p.last_name}-${p.date_of_birth}`)
          );
          if (!memberFound || renderable.length === 0) return null;
          return (
            <div className={`rounded-lg border p-6 ${isEmbedDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isEmbedDark ? "text-gray-100" : "text-gray-900"}`}>
                Additional Participants
              </h3>
              <div className="space-y-4">
                {participants.map((p, index) => {
                  if (p.athlete_id) return null;
                  if (syntheticSelectedKeys.has(`${p.first_name}-${p.last_name}-${p.date_of_birth}`)) return null;
                  return (
                  <div key={`manual-${index}`} className={`p-4 rounded-lg border ${isEmbedDark ? "border-gray-600 bg-gray-700" : "border-gray-100 bg-gray-50"}`}>
                    <div className="flex justify-between items-center mb-3">
                      <span className={`text-sm font-medium ${isEmbedDark ? "text-gray-200" : "text-gray-700"}`}>
                        {p.first_name && p.last_name ? `${p.first_name} ${p.last_name}` : "New Participant"}
                      </span>
                      <button aria-label={`Remove participant ${index + 1}`}
                        type="button"
                        onClick={() => removeParticipant(index)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        aria-label={`Participant ${index + 1} first name`}
                        placeholder="First Name *"
                        value={p.first_name}
                        onChange={(e) => updateParticipant(index, "first_name", e.target.value)}
                        className={`w-full border rounded-md px-3 py-2 text-sm ${
                        isEmbedDark ? "border-gray-600 bg-gray-800 text-gray-100" : "border-gray-300"
                      }`}
                      />
                      <input
                        type="text"
                        aria-label={`Participant ${index + 1} last name`}
                        placeholder="Last Name *"
                        value={p.last_name}
                        onChange={(e) => updateParticipant(index, "last_name", e.target.value)}
                        className={`w-full border rounded-md px-3 py-2 text-sm ${
                        isEmbedDark ? "border-gray-600 bg-gray-800 text-gray-100" : "border-gray-300"
                      }`}
                      />
                      <input
                        type="date" autoComplete="off"
                        aria-label={`Participant ${index + 1} date of birth`}
                        value={p.date_of_birth}
                        onChange={(e) => updateParticipant(index, "date_of_birth", e.target.value)}
                        className={`w-full border rounded-md px-3 py-2 text-sm ${
                        isEmbedDark ? "border-gray-600 bg-gray-800 text-gray-100" : "border-gray-300"
                      }`}
                      />
                      <select
                        value={p.gender}
                        onChange={(e) => updateParticipant(index, "gender", e.target.value)}
                        className={`w-full border rounded-md px-3 py-2 text-sm ${
                        isEmbedDark ? "border-gray-600 bg-gray-800 text-gray-100" : "border-gray-300"
                      }`}
                      >
                        <option value="">Gender *</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        {typedEvent.allow_other_gender && (
                          <>
                            <option value="other">Other</option>
                            <option value="prefer_not_to_say">Prefer not to say</option>
                          </>
                        )}
                      </select>
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          );
        })()}

        {/* Pricing Option */}
        {typedEvent.pricing_options && typedEvent.pricing_options.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Registration Option</h3>
            <div className="space-y-3">
              {typedEvent.pricing_options.map((option, index) => (
                <label
                  key={index}
                  className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedPricing?.label === option.label
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                >
                  <div className="flex items-center">
                    <input
                      type="radio"
                      name="pricing"
                      checked={selectedPricing?.label === option.label}
                      onChange={() => setSelectedPricing(option)}
                      className="mr-3"
                    />
                    <span className="text-gray-900 font-medium">{option.label}</span>
                  </div>
                  <span className="text-gray-700 font-semibold">
                    {option.amount > 0 ? `$${option.amount.toFixed(2)}` : "Free"}
                  </span>
                </label>
              ))}
            </div>

            {/* Pricing summary for multiple participants */}
            {effectiveParticipants.length > 1 && selectedPricing && selectedPricing.amount > 0 && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">
                    {effectiveParticipants.length} participants x ${selectedPricing.amount.toFixed(2)}
                  </span>
                  <span className="text-lg font-bold text-gray-900">
                    ${getTotalAmount().toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Emergency Contact & Medical */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Emergency Contact & Medical</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Input
                label="Emergency Contact Name"
                type="text"
                name="emergency_contact_name"
                value={emergencyInfo.emergency_contact_name}
                onChange={handleEmergencyChange}
                className="border-gray-300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Emergency Contact Phone
              </label>
              <PhoneInput
                name="emergency_contact_phone"
                value={emergencyInfo.emergency_contact_phone}
                onChange={(d) =>
                  handleEmergencyChange({
                    target: { name: "emergency_contact_phone", value: formatPartialNational(d) },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <Input
                label="Relationship"
                type="text"
                name="emergency_contact_relationship"
                value={emergencyInfo.emergency_contact_relationship}
                onChange={handleEmergencyChange}
                className="border-gray-300"
                placeholder="e.g., Parent, Guardian"
              />
            </div>
          </div>
          <div className="mt-4 space-y-4">
            <div>
              <Input
                label="Allergies"
                type="text"
                name="allergies"
                value={emergencyInfo.allergies}
                onChange={handleEmergencyChange}
                className="border-gray-300"
                placeholder="List any allergies"
              />
            </div>
            <div>
              <Input
                label="Medications"
                type="text"
                name="medications"
                value={emergencyInfo.medications}
                onChange={handleEmergencyChange}
                className="border-gray-300"
                placeholder="List any medications"
              />
            </div>
            <div>
              <Textarea
                label="Medical Information"
                name="medical_info"
                value={emergencyInfo.medical_info}
                onChange={handleEmergencyChange}
                className="border-gray-300"
                rows={2}
                placeholder="Any medical conditions or special needs"
              />
            </div>
            <div>
              <Textarea
                label="Additional Notes"
                name="notes"
                value={emergencyInfo.notes}
                onChange={handleEmergencyChange}
                className="border-gray-300"
                rows={2}
                placeholder="Any additional information"
              />
            </div>
          </div>
        </div>

        <PhotoReleaseQuestion photo={photo} />

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full py-3 bg-[var(--cc-action)] text-[var(--cc-action-text)] hover:bg-[var(--cc-action-hover)] rounded-lg font-medium disabled:opacity-50 transition-colors text-lg"
        >
          {submitting
            ? "Registering..."
            : waitlistOnly
              ? "Join the Waitlist"
              : getTotalAmount() > 0
              ? `Register & Pay $${getTotalAmount().toFixed(2)}`
              : "Register"}
        </button>
      </form>
    );
  };

  // Main page component
  interface PublicTypedEventPageProps {
    embed?: boolean;
  }

  const PublicTypedEventPage: React.FC<PublicTypedEventPageProps> = ({ embed }) => {
    const { clubid, [T.idParam]: typedEventId } = useParams<{ clubid: string; campId: string; clinicId: string }>();
    const [typedEvent, setTypedEvent] = useState<PublicTypedEvent | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      if (clubid && typedEventId) {
        fetchTypedEvent();
      }
    }, [clubid, typedEventId]);

    const fetchTypedEvent = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getPublicTypedEventById(clubid!, typedEventId!);
        if (response?.success) {
          setTypedEvent(response.data);
        } else {
          setError(`${T.Noun} not found`);
        }
      } catch {
        setError(`Failed to load ${T.noun}. This ${T.noun} may not exist or is no longer available.`);
      } finally {
        setLoading(false);
      }
    };

    const formatDate = (dateString: string) => {
      try {
        const [year, month, day] = dateString.split("-").map(Number);
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      } catch {
        return dateString;
      }
    };

    const formatTime = (timeString: string) => {
      if (!timeString) return "";
      const [hours, minutes] = timeString.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    };

    const isFull = typedEvent
      ? typedEvent.max_registrations
        ? typedEvent.current_registrations >= typedEvent.max_registrations && !typedEvent.waitlist_enabled
        : false
      : false;

    const isDeadlinePassed = typedEvent?.registration_deadline
      ? new Date() > new Date(typedEvent.registration_deadline)
      : false;

    if (loading) {
      return (
        <div className={`flex flex-col min-h-screen ${embed ? "" : "bg-gray-50"}`}>
          {!embed && <Navbar />}
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400 dark:border-gray-500"></div>
          </div>
          {!embed && <Footer />}
        </div>
      );
    }

    if (error || !typedEvent) {
      return (
        <div className={`flex flex-col min-h-screen ${embed ? "" : "bg-gray-50"}`}>
          {!embed && <Navbar />}
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-500 mb-2">{T.Noun} Not Found</h2>
              <p className="text-gray-400 mb-4">{error}</p>
              <Link
                to={embed ? `/${clubid}/embed/${T.nouns}` : `/${clubid}/${T.nouns}`}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Browse all {T.nouns}
              </Link>
            </div>
          </div>
          {!embed && <Footer />}
        </div>
      );
    }

    return (
      <div className={`flex flex-col min-h-screen ${embed ? "" : "bg-gray-50"}`}>
        {!embed && <Navbar />}
        {/* Page title + Open Graph tags. Only the clinics copy had them. */}
        <SEOHead
          title={`${typedEvent.name} - ${T.Noun} Registration`}
          description={typedEvent.description || `Register for ${typedEvent.name}`}
          ogType="website"
          ogImage={typedEvent.cover_image_url || undefined}
        />
        <div className="flex-1 max-w-6xl mx-auto px-4 py-8 w-full">
          {/* Back link */}
          <Link
            to={embed ? `/${clubid}/embed/${T.nouns}` : `/${clubid}/${T.nouns}`}
            className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to {T.nouns}
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Camp Details - Left Column */}
            <div className="lg:col-span-2 space-y-6">
              {typedEvent.cover_image_url && (
                <img
                  src={typedEvent.cover_image_url}
                  alt={typedEvent.name}
                  className="w-full h-64 object-cover rounded-lg"
                />
              )}

              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{typedEvent.name}</h1>

                <div className="flex flex-wrap gap-2 mb-4">
                  {typedEvent[T.subtype] && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                      {typedEventTypeLabel(typedEvent[T.subtype])}
                    </span>
                  )}
                  {typedEvent.skill_level && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                      {skillLevelLabel(typedEvent.skill_level)}
                    </span>
                  )}
                  {typedEvent.members_only && (
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                      Members Only
                    </span>
                  )}
                </div>

                {typedEvent.description && (
                  <p className="text-gray-600 whitespace-pre-wrap">{typedEvent.description}</p>
                )}
              </div>

              {/* Dates */}
              {typedEvent.dates && typedEvent.dates.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-blue-500" />
                    Schedule
                  </h2>
                  <div className="space-y-3">
                    {typedEvent.dates.map((dateItem, index) => (
                      <div
                        key={index}
                        className="flex items-start p-3 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-gray-900">
                            {formatDate(dateItem.date)}
                          </p>
                          <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                            {dateItem.start_time && (
                              <span className="flex items-center">
                                <Clock className="w-3.5 h-3.5 mr-1" />
                                {formatTime(dateItem.start_time)}
                                {dateItem.end_time && ` - ${formatTime(dateItem.end_time)}`}
                              </span>
                            )}
                            {dateItem.location && (
                              <span className="flex items-center">
                                <MapPin className="w-3.5 h-3.5 mr-1" />
                                {dateItem.location}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* What to Bring / Cancellation */}
              {(typedEvent.what_to_bring || typedEvent.cancellation_policy) && (
                <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
                  {typedEvent.what_to_bring && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">What to Bring</h3>
                      <p className="text-gray-600 whitespace-pre-wrap">{typedEvent.what_to_bring}</p>
                    </div>
                  )}
                  {typedEvent.cancellation_policy && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Cancellation Policy</h3>
                      <p className="text-gray-600 whitespace-pre-wrap">{typedEvent.cancellation_policy}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Registration Sidebar - Right Column */}
            <div className="lg:col-span-1">
              {/* Quick Info Card */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Details</h2>
                <div className="space-y-3 text-sm">
                  {typedEvent.pricing_options && typedEvent.pricing_options.length > 0 && (
                    <div className="flex items-start">
                      <DollarSign className="w-4 h-4 mr-2 mt-0.5 text-gray-400" />
                      <div>
                        {typedEvent.pricing_options.map((p, i) => (
                          <div key={i}>
                            <span className="font-medium">{p.label}:</span>{" "}
                            {p.amount > 0 ? `$${p.amount.toFixed(2)}` : "Free"}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(typedEvent.age_min || typedEvent.age_max) && (
                    <div className="flex items-center">
                      <Users className="w-4 h-4 mr-2 text-gray-400" />
                      <span>
                        Ages {typedEvent.age_min || "Any"} - {typedEvent.age_max || "Any"}
                      </span>
                    </div>
                  )}

                  {typedEvent.max_registrations && (
                    <div className="flex items-center">
                      <Users className="w-4 h-4 mr-2 text-gray-400" />
                      <span>
                        {typedEvent.current_registrations} / {typedEvent.max_registrations} registered
                      </span>
                    </div>
                  )}

                  {typedEvent.registration_deadline && (
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                      <span>
                        Register by {formatDate(typedEvent.registration_deadline.split("T")[0])}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Registration blocked messages */}
              {isDeadlinePassed && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <p className="text-yellow-700 font-medium">Registration deadline has passed.</p>
                </div>
              )}

              {isFull && !isDeadlinePassed && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <p className="text-red-700 font-medium">This {T.noun} is full.</p>
                </div>
              )}

              {/* Registration Form or Members-Only CTA */}
              {!isDeadlinePassed && !isFull && (
                typedEvent.members_only ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
                    <p className="text-gray-700 font-medium mb-2">
                      This {T.noun} is for members only.
                    </p>
                    <p className="text-gray-500 text-sm mb-4">
                      An active membership is required to participate.
                    </p>
                    <Link
                      to={embed ? `/${clubid}/embed/register` : `/${clubid}/login`}
                      className="inline-block px-8 py-3 bg-[var(--cc-action)] text-[var(--cc-action-text)] hover:bg-[var(--cc-action-hover)] rounded-md font-semibold transition-colors"
                    >
                      {embed ? "Register for Membership" : "Register / Login"}
                    </Link>
                  </div>
                ) : (
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">
                      {joinsWaitlist(typedEvent) ? "Join the Waitlist" : "Register"}
                    </h2>
                    {joinsWaitlist(typedEvent) && (
                      <p className="text-sm text-gray-600 mb-4" data-testid="waitlist-note">
                        This {T.noun} is full. Join the waitlist and the club will contact you if a place opens. No payment is taken now.
                      </p>
                    )}
                    {/* No outer <Elements> wrapper — the form step
                        doesn't use Stripe, and the payment step
                        mounts its own <Elements> with clientSecret
                        so PaymentElement can surface saved methods. */}
                    <RegistrationForm typedEvent={typedEvent} clubid={clubid!} embed={embed} />
                  </div>
                )
              )}
            </div>
          </div>
        </div>
        {!embed && <Footer />}
      </div>
    );
  };

  PublicTypedEventPage.displayName = `${T.Noun}PublicTypedEventPage`;
  return PublicTypedEventPage;
}
